import React, { useEffect, useState } from 'react';
import { differenceInMilliseconds } from 'date-fns';
import {
  useIsBeforeCallCanStart,
  useIsAfterCallCanStart,
  useEarliestStartTime,
  useLatestStartTime
} from '@components/Clinicians/Session/Components/CallMenu';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import MediaPermissionsWarning from './MediaPermissionsWarning';

/**
 * Clinician-specific wrapper for MediaPermissionsWarning.
 * Calculates calling window based on session appointment timing.
 * Uses the same timing mechanism as the "Video call" button.
 */
const ClinicianMediaPermissionsWarning = () => {
  const [, setRefresh] = useState(0);
  const appointmentId = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();

  const isBeforeCallCanStart = useIsBeforeCallCanStart();
  const isAfterCallCanStart = useIsAfterCallCanStart();
  const earliestStartTime = useEarliestStartTime();
  const latestStartTime = useLatestStartTime();

  const isInCallingWindow = !isBeforeCallCanStart && !isAfterCallCanStart && !appointment?.appointment_completed;

  useEffect(() => {
    if (isBeforeCallCanStart) {
      const timer = setTimeout(() => setRefresh((r) => r + 1), differenceInMilliseconds(earliestStartTime, new Date()));
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isBeforeCallCanStart, earliestStartTime]);

  useEffect(() => {
    if (!isAfterCallCanStart) {
      const timer = setTimeout(() => setRefresh((r) => r + 1), differenceInMilliseconds(latestStartTime, new Date()));
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isAfterCallCanStart, latestStartTime]);

  const storageKey = appointmentId ? `clinician-${appointmentId}` : undefined;
  const callType = appointment?.call_type_code as 'AUDIO' | 'VIDEO' | undefined;

  return <MediaPermissionsWarning isInCallingWindow={isInCallingWindow} storageKey={storageKey} callType={callType} />;
};

export default ClinicianMediaPermissionsWarning;
